import type { AssignableRole } from "@unbox/api";
import { useEffect, useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useListData } from "react-stately";
import { useSendInvite } from "@/api/admin/send-invite";
import { useAdminWorkspaces } from "@/api/admin/workspaces";
import { AssignableRoleDescriptions, AssignableRoleLabels } from "@/components/admin/role-label";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { MultiSelect } from "@/components/base/select/multi-select";
import { Select } from "@/components/base/select/select";
import { FormField, FormItem } from "@/components/ui/form";
import { usePermissions } from "@/hooks/use-permissions";
import { error, success } from "@/providers/ToastProvider";

type FormValues = {
  role: AssignableRole;
  email: string;
};

export const SendInviteModal = () => {
  const [open, setOpen] = useState(false);
  const form = useForm<FormValues>({
    defaultValues: { role: "user", email: "" },
  });
  const { mutateAsync } = useSendInvite();
  const { invitableRoles } = usePermissions();
  const { data: workspacesData } = useAdminWorkspaces(
    { offset: 0, limit: 100 },
    { column: "name", direction: "asc" },
    {},
  );
  const selectedWorkspaces = useListData<{ id: string; label: string }>({ initialItems: [] });
  const [workspaceBrandSelections, setWorkspaceBrandSelections] = useState<Record<string, Set<string>>>({});

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on modal open/close
  useEffect(() => {
    form.reset();
    for (const item of [...selectedWorkspaces.items]) selectedWorkspaces.remove(item.id);
    selectedWorkspaces.setSelectedKeys(new Set());
    setWorkspaceBrandSelections({});
  }, [open]);

  useEffect(() => {
    const currentIds = new Set(selectedWorkspaces.items.map((w) => w.id));
    setWorkspaceBrandSelections((prev) => {
      const next: Record<string, Set<string>> = {};
      for (const id of Object.keys(prev)) {
        if (currentIds.has(id)) next[id] = prev[id];
      }
      return next;
    });
  }, [selectedWorkspaces.items]);

  const workspacesById = new Map((workspacesData?.workspaces ?? []).map((ws) => [ws.id, ws]));

  const toggleBrand = (workspaceId: string, brandId: string) => {
    setWorkspaceBrandSelections((prev) => {
      const current = new Set(prev[workspaceId] ?? []);
      if (current.has(brandId)) current.delete(brandId);
      else current.add(brandId);
      return { ...prev, [workspaceId]: current };
    });
  };

  const toggleAllBrands = (workspaceId: string, allIds: string[], selectAll: boolean) => {
    setWorkspaceBrandSelections((prev) => ({
      ...prev,
      [workspaceId]: selectAll ? new Set(allIds) : new Set(),
    }));
  };

  const onSubmit: SubmitHandler<FormValues> = async (payload) => {
    const workspaceBrands: Record<string, string[]> = {};
    for (const ws of selectedWorkspaces.items) {
      const brandIds = Array.from(workspaceBrandSelections[ws.id] ?? []);
      if (brandIds.length === 0) {
        error(`Select at least one merchant in "${ws.label}"`);
        return;
      }
      workspaceBrands[ws.id] = brandIds;
    }
    if (Object.keys(workspaceBrands).length === 0) {
      error("Select at least one workspace and merchant");
      return;
    }
    await mutateAsync({
      email: payload.email.trim(),
      role: payload.role,
      workspaceBrands,
    });
    success("User invited");
    setOpen(false);
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button>Send Invite</Button>
      {/* Not dismissable: an outside click would discard the whole form (UNB-2129). */}
      <ModalOverlay isDismissable={false}>
        <Modal className="dark-mode w-xl">
          <Dialog className="w-full">
            <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
              <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all sm:w-full">
                <CloseButton
                  onClick={() => setOpen(false)}
                  theme="light"
                  size="lg"
                  className="absolute top-3 right-3"
                />
                <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                  <Heading slot="title" className="text-md font-semibold text-primary">
                    Send Invite
                  </Heading>
                </div>
                <div className="h-5 w-full" />
                <div className="relative flex flex-col gap-4 px-4 sm:gap-5 sm:px-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Input isRequired label="Email" placeholder="Email" {...field} />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Select
                          label="Role"
                          placeholder="Select user role"
                          isRequired
                          size="md"
                          stackSupportingText
                          items={invitableRoles.map((r) => ({
                            id: r,
                            label: AssignableRoleLabels[r],
                            supportingText: AssignableRoleDescriptions[r],
                          }))}
                          selectedKey={field.value}
                          onSelectionChange={(v) => field.onChange(v)}
                          popoverClassName="dark-mode"
                        >
                          {(item) => (
                            <Select.Item
                              key={item.id}
                              id={item.id}
                              supportingText={item.supportingText}
                              className="dark-mode"
                            >
                              {item.label}
                            </Select.Item>
                          )}
                        </Select>
                      </FormItem>
                    )}
                  />
                  <FormItem className="flex flex-col">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-sm font-medium text-primary">Workspaces</span>
                    </div>
                    <MultiSelect
                      placeholder="Search Workspaces"
                      size="md"
                      items={
                        workspacesData?.workspaces.map((ws) => ({
                          id: ws.id,
                          label: ws.name,
                        })) ?? []
                      }
                      selectedItems={selectedWorkspaces}
                      popoverClassName="dark-mode"
                    >
                      {(item) => (
                        <MultiSelect.Item key={item.id} id={item.id} className="dark-mode">
                          {item.label}
                        </MultiSelect.Item>
                      )}
                    </MultiSelect>
                  </FormItem>

                  {selectedWorkspaces.items.length > 0 && (
                    <div className="flex flex-col gap-3">
                      {selectedWorkspaces.items.map((ws) => {
                        const workspace = workspacesById.get(ws.id);
                        const brands = workspace?.merchants ?? [];
                        const selected = workspaceBrandSelections[ws.id] ?? new Set<string>();
                        const allSelected = brands.length > 0 && brands.every((b) => selected.has(b.id));
                        return (
                          <div key={ws.id} className="flex flex-col gap-2 rounded-lg border border-secondary p-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-primary">{ws.label} merchants</span>
                              <button
                                type="button"
                                className="text-xs text-brand-secondary font-semibold"
                                onClick={() =>
                                  toggleAllBrands(
                                    ws.id,
                                    brands.map((b) => b.id),
                                    !allSelected,
                                  )
                                }
                              >
                                {allSelected ? "Clear" : "Select all merchants"}
                              </button>
                            </div>
                            {brands.length === 0 ? (
                              <p className="text-sm text-tertiary">No merchants in this workspace yet.</p>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {brands.map((brand) => {
                                  const isSelected = selected.has(brand.id);
                                  return (
                                    <button
                                      type="button"
                                      key={brand.id}
                                      onClick={() => toggleBrand(ws.id, brand.id)}
                                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                                        isSelected
                                          ? "bg-brand-solid text-white border-transparent"
                                          : "bg-primary text-secondary border-secondary"
                                      }`}
                                    >
                                      {brand.name}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                  <Button color="secondary" size="lg" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button color="primary" size="lg" type="submit">
                    Invite
                  </Button>
                </div>
              </div>
            </form>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
